import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { AppBreadcrumbComponent } from "../../../layouts/full/shared/breadcrumb/breadcrumb.component";
import { MaterialModule } from 'src/app/material.module';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { Subject, Observable, takeUntil, debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { MediclaimDetailComponent } from './mediclaim-detail/mediclaim-detail.component';
import { CommonModule } from '@angular/common';
import { MediclaimService } from './mediclaim.service';
import { DeleteDialog } from '../../dialogs/delete.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PaginationRequest } from 'src/app/models/common.request';
import { IMediclaim } from './imediclaim';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-mediclaim',
  standalone: true,
  imports: [
    AppBreadcrumbComponent,
    MaterialModule,
    ReactiveFormsModule,
    TablerIconsModule,
    CommonModule,
    TranslateModule
  ],
  templateUrl: './mediclaim.component.html',
  styleUrl: './mediclaim.component.scss'
})
export class MediclaimComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'mediclaimName',
    'weburl',
    'default',
    'action',
  ];
  dataSource = new MatTableDataSource<IMediclaim>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  pagination: PaginationResult;
  mediclaimcount: number = 0;
  mediclaims$: Observable<IMediclaim[]>;
  mediclaimdata: IMediclaim[];
  isLoading: boolean = false;
  isLoadData: boolean;
  searchmediclaim: FormControl = new FormControl();
  searchQuery: string;
  pageSizeOptions = PAGE_SIZE_OPTIONS;


  constructor(
    private _mediclaimService: MediclaimService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnInit(): void {
    this._mediclaimService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.mediclaimcount = this.pagination.rowCount;
        }
      });

    this._mediclaimService.mediclaims$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.mediclaimdata = data;
        this.dataSource = new MatTableDataSource(this.mediclaimdata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });

    this.searchmediclaim.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        return this._mediclaimService.getMediclaimPlan({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || ''
        }).pipe(
          finalize(() => (this.isLoading = false))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.mediclaims;
        this.mediclaimcount = response.result.pagination.rowCount;
      }
    });

  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onAddMediclaim() {
    const dialogRef = this.dialog.open(MediclaimDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshMediclaim();
      }
    });
  }

  onEditMediclaim(mediclaim: IMediclaim) {
    const dialogRef = this.dialog.open(MediclaimDetailComponent, {
      width: '600px',
      disableClose: true,
      data: {
        mediclaimdata: mediclaim,
        isEdit: true,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshMediclaim();
      }
    });
  }

  onDeleteMediclaim(id: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.MEDICLAIM') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._mediclaimService.deleteMediclaim(id).subscribe((response) => {
          if (response.isSuccess) {
            this.refreshMediclaim();
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.MEDICLAIM')));
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));

          }
        })
      }
    });
  }

  private refreshMediclaim() {
    const globalSearchValue = this.searchmediclaim?.value?.trim();
    const requestPayload: PaginationRequest = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.searchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._mediclaimService.getMediclaimPlan(requestPayload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        if (response && response.result) {
          const mediclaim = response.result.mediclaims;
          this.mediclaimdata = mediclaim;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(mediclaim);
          } else {
            this.dataSource.data = mediclaim;
          }
          this.dataSource.paginator = this.paginator;
          this.mediclaimcount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });
  }

}
